<template>
  <div class="candidate-recommend-page">
    <div class="form-shell" v-loading="pageLoading" element-loading-text="正在加载申请信息...">
      <el-alert
        class="form-tip"
        :title="isEditMode ? '正在修改委员推荐申请，可返回上一步重新选择专委会。' : '请先选择专委会，再填写委员候选人推荐表。'"
        type="info"
        show-icon
        :closable="false"
      />
      <el-steps :active="isEditMode ? 1 : currentStep" finish-status="success" align-center class="form-steps">
        <el-step title="选择专委会" />
        <el-step title="填写推荐表" />
      </el-steps>

      <section v-if="currentStep === 0" class="committee-step">
        <h1>选择专委会</h1>
        <div class="committee-groups">
          <section v-for="group in committeeOptions" :key="group.id || group.name" class="committee-group">
            <h2>{{ group.name }}</h2>
            <div class="committee-grid">
              <button
                v-for="item in getCommitteeChildren(group)"
                :key="item.id || item.name"
                type="button"
                class="committee-card"
                :class="{ active: isCommitteeSelected(item) }"
                @click="chooseCommittee(group, item)"
              >
                {{ item.name }}
              </button>
            </div>
          </section>
        </div>
        <div class="step-actions">
          <el-button type="primary" @click="goNextStep">下一步</el-button>
        </div>
      </section>

      <template v-else>
        <header class="page-header">
          <div class="association-line">
            <span>山东省心理咨询行业协会</span>
            <span class="committee-name">{{ form.committee }}</span>
          </div>
          <h1>委员候选人推荐表</h1>
          <div class="serial-no">
            <span class="span-width">编号：</span>
            <el-input v-model="form.serialNo" readonly />
          </div>
        </header>

        <el-form ref="formRef" :model="form" :rules="rules" label-position="top" class="recommend-form">
          <div class="paper-form">
            <table class="recommend-table">
              <tbody>
                <tr>
                  <th>姓名</th>
                  <td><el-form-item prop="name"><el-input v-model="form.name" /></el-form-item></td>
                  <th>性别</th>
                  <td><el-form-item prop="gender"><el-input v-model="form.gender" /></el-form-item></td>
                  <th>出生年月</th>
                  <td><el-form-item prop="birthday"><el-date-picker v-model="form.birthday" type="month" value-format="YYYY-MM" /></el-form-item></td>
                  <td rowspan="5" class="photo-cell">
                    <el-upload
                      class="photo-uploader"
                      action="#"
                      :auto-upload="false"
                      :show-file-list="false"
                      :on-change="handlePhotoChange"
                      :disabled="photoUploading"
                      accept="image/*"
                    >
                      <img v-if="form.photoUrl" :src="form.photoUrl" alt="照片" class="photo-preview">
                      <div v-else class="photo-placeholder">{{ photoUploading ? '上传中...' : '（上传1寸照片，必填）' }}</div>
                    </el-upload>
                  </td>
                </tr>
                <tr>
                  <th>籍贯</th>
                  <td><el-form-item prop="nativePlace"><el-input v-model="form.nativePlace" /></el-form-item></td>
                  <th>民族</th>
                  <td><el-form-item prop="nation"><el-input v-model="form.nation" /></el-form-item></td>
                  <th>身份证号</th>
                  <td><el-form-item prop="idNumber"><el-input v-model="form.idNumber" /></el-form-item></td>
                </tr>
                <tr>
                  <th>入会时间</th>
                  <td colspan="3"><el-form-item prop="joinDate"><el-date-picker v-model="form.joinDate" type="month" value-format="YYYY-MM" /></el-form-item></td>
                  <th>会员证号</th>
                  <td><el-form-item prop="memberNo"><el-input v-model="form.memberNo" /></el-form-item></td>
                </tr>
                <tr>
                  <th>工作单位</th>
                  <td colspan="3"><el-form-item prop="company"><el-input v-model="form.company" /></el-form-item></td>
                  <th>技术职称</th>
                  <td><el-form-item prop="title"><el-input v-model="form.title" /></el-form-item></td>
                </tr>
                <tr>
                  <th>联系地址</th>
                  <td colspan="3"><el-form-item prop="address"><el-input v-model="form.address" /></el-form-item></td>
                  <th>邮编</th>
                  <td><el-form-item prop="postalCode"><el-input v-model="form.postalCode" /></el-form-item></td>
                </tr>
                <tr>
                  <th>通讯方式</th>
                  <td colspan="2">手机：<el-form-item prop="mobile"><el-input v-model="form.mobile" /></el-form-item></td>
                  <td colspan="2">办公：<el-form-item prop="officePhone"><el-input v-model="form.officePhone" /></el-form-item></td>
                  <td colspan="2">Email：<el-form-item prop="email"><el-input v-model="form.email" /></el-form-item></td>
                </tr>

                <tr>
                  <th rowspan="4" class="vertical-title"><span>主要学历</span></th>
                  <th colspan="2">起止年月</th>
                  <th colspan="2">院校名称</th>
                  <th colspan="2">学位</th>
                </tr>
                <tr v-for="(item, index) in form.educationList" :key="`edu-${index}`">
                  <td colspan="2"><el-input v-model="item.period" /></td>
                  <td colspan="2"><el-input v-model="item.school" /></td>
                  <td colspan="2"><el-input v-model="item.degree" /></td>
                </tr>

                <tr>
                  <th rowspan="4" class="vertical-title"><span>主要工作经历</span></th>
                  <th colspan="2">起止年月</th>
                  <th colspan="2">工作单位</th>
                  <th colspan="2">职务和技术职称</th>
                </tr>
                <tr v-for="(item, index) in form.workList" :key="`work-${index}`">
                  <td colspan="2"><el-input v-model="item.period" /></td>
                  <td colspan="2"><el-input v-model="item.company" /></td>
                  <td colspan="2"><el-input v-model="item.position" /></td>
                </tr>

                <tr>
                  <th rowspan="5" class="vertical-title"><span>主要培训经历</span></th>
                  <th colspan="2">起止时间</th>
                  <th colspan="2">培训项目/课程名称</th>
                  <th colspan="2">是否为注册系统认证课程</th>
                </tr>
                <tr v-for="(item, index) in form.trainingList" :key="`training-${index}`">
                  <td colspan="2"><el-input v-model="item.period" /></td>
                  <td colspan="2"><el-input v-model="item.course" /></td>
                  <td colspan="2">
                    <el-radio-group v-model="item.certified">
                      <el-radio label="是">是</el-radio>
                      <el-radio label="否">否</el-radio>
                    </el-radio-group>
                  </td>
                </tr>

                <tr>
                  <th colspan="2">其他社会兼职</th>
                  <td colspan="3"><el-input v-model="form.partTimeJob" /></td>
                  <th>掌握外语情况</th>
                  <td><el-input v-model="form.foreignLanguage" /></td>
                </tr>
                <tr>
                  <th colspan="2">本人承诺</th>
                  <td colspan="5">
                    <el-checkbox v-model="form.promise">本人所提供的个人信息真实、准确；并同意提交推荐材料。</el-checkbox>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mobile-form">
            <section v-for="section in mobileSections" :key="section.title" class="mobile-section">
              <h2>{{ section.title }}</h2>
              <template v-for="field in section.fields" :key="field.key">
                <el-form-item :label="field.label" :prop="field.prop || field.key">
                  <el-date-picker
                    v-if="field.type === 'month'"
                    v-model="form[field.key]"
                    type="month"
                    value-format="YYYY-MM"
                    placeholder="请选择"
                  />
                  <el-input
                    v-else-if="field.type === 'textarea'"
                    v-model="form[field.key]"
                    type="textarea"
                    :rows="field.rows || 4"
                  />
                  <el-input v-else v-model="form[field.key]" :readonly="field.readonly" />
                </el-form-item>
              </template>
            </section>

            <section class="mobile-section">
              <h2>照片</h2>
              <el-upload action="#" :auto-upload="false" :show-file-list="false" :on-change="handlePhotoChange" :disabled="photoUploading" accept="image/*">
                <div class="mobile-photo">
                  <img v-if="form.photoUrl" :src="form.photoUrl" alt="照片">
                  <span v-else>{{ photoUploading ? '上传中...' : '上传照片（必填）' }}</span>
                </div>
              </el-upload>
            </section>

            <section class="mobile-section" v-for="group in mobileRepeatGroups" :key="group.title">
              <h2>{{ group.title }}</h2>
              <div v-for="(item, index) in form[group.key]" :key="`${group.key}-${index}`" class="repeat-card">
                <el-form-item v-for="field in group.fields" :key="field.key" :label="field.label" required>
                  <el-radio-group v-if="field.type === 'radio'" v-model="item[field.key]">
                    <el-radio label="是">是</el-radio>
                    <el-radio label="否">否</el-radio>
                  </el-radio-group>
                  <el-input v-else v-model="item[field.key]" />
                </el-form-item>
              </div>
            </section>

            <section class="mobile-section">
              <h2>补充信息</h2>
              <el-form-item label="其他社会兼职"><el-input v-model="form.partTimeJob" /></el-form-item>
              <el-form-item label="掌握外语情况"><el-input v-model="form.foreignLanguage" /></el-form-item>
              <el-form-item>
                <el-checkbox v-model="form.promise">本人所提供的个人信息真实、准确；并同意提交推荐材料。</el-checkbox>
              </el-form-item>
            </section>
          </div>

          <div class="form-actions">
            <el-button @click="currentStep = 0">上一步</el-button>
            <el-button @click="resetForm">重置</el-button>
            <el-button @click="goList">返回列表</el-button>
            <el-button type="primary" :loading="submitting" @click="submitForm">{{ editingId ? '提交修改' : '提交推荐表' }}</el-button>
          </div>
        </el-form>
      </template>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import attachment from '@/api/public'
import dictApi from '@/api/dict'
import candidateRecommend from '@/api/candidateRecommend'

const createRows = (count, row) => Array.from({ length: count }, () => ({ ...row }))
const certificateNoPattern = /^SDXLHY-[A-Z]{2}-\d{9}$/

const defaultForm = () => ({
  committee: '',
  serialNo: '',
  name: '',
  gender: '',
  birthday: '',
  nativePlace: '',
  nation: '',
  idNumber: '',
  joinDate: '',
  memberNo: '',
  company: '',
  title: '',
  address: '',
  postalCode: '',
  mobile: '',
  officePhone: '',
  email: '',
  photoUrl: '',
  educationList: createRows(3, { period: '', school: '', degree: '' }),
  workList: createRows(3, { period: '', company: '', position: '' }),
  trainingList: createRows(4, { period: '', course: '', certified: '' }),
  partTimeJob: '',
  foreignLanguage: '',
  promise: false
})

const requiredRule = (message, trigger = 'blur') => [{ required: true, message, trigger }]
const memberNoRule = (rule, value, callback) => {
  const text = String(value || '').trim().toUpperCase()
  if (!text) {
    callback(new Error('请填写会员证号'))
    return
  }
  if (!certificateNoPattern.test(text)) {
    callback(new Error('会员证号应为电子证书编号'))
    return
  }
  callback()
}

export default {
  name: 'CandidateRecommendApply',
  data() {
    return {
      currentStep: 0,
      form: defaultForm(),
      isEditMode: Number(this.$route.query.id || 0) > 0,
      editingId: 0,
      pageLoading: false,
      listLoading: false,
      photoUploading: false,
      submitting: false,
      applicationList: [],
      committeeValue: [],
      committeeOptions: [],
      rules: {
        name: requiredRule('请填写姓名'),
        gender: requiredRule('请填写性别'),
        birthday: requiredRule('请选择出生年月', 'change'),
        nativePlace: requiredRule('请填写籍贯'),
        nation: requiredRule('请填写民族'),
        idNumber: requiredRule('请填写身份证号'),
        joinDate: requiredRule('请选择入会时间', 'change'),
        memberNo: [{ validator: memberNoRule, trigger: 'blur' }],
        company: requiredRule('请填写工作单位'),
        title: requiredRule('请填写技术职称'),
        address: requiredRule('请填写联系地址'),
        postalCode: requiredRule('请填写邮编'),
        mobile: [
          { required: true, message: '请填写手机号码', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请填写正确的手机号码', trigger: 'blur' }
        ],
        officePhone: requiredRule('请填写办公电话'),
        email: [
          { required: true, message: '请填写电子邮箱', trigger: 'blur' },
          { type: 'email', message: '请填写正确的邮箱地址', trigger: 'blur' }
        ]
      },
      mobileSections: [
        {
          title: '基本信息',
          fields: [
            { key: 'serialNo', label: '编号', readonly: true },
            { key: 'name', label: '姓名' },
            { key: 'gender', label: '性别' },
            { key: 'birthday', label: '出生年月', type: 'month' },
            { key: 'nativePlace', label: '籍贯' },
            { key: 'nation', label: '民族' },
            { key: 'idNumber', label: '身份证号' }
          ]
        },
        {
          title: '会员与联系信息',
          fields: [
            { key: 'joinDate', label: '入会时间', type: 'month' },
            { key: 'memberNo', label: '会员证号' },
            { key: 'company', label: '工作单位' },
            { key: 'title', label: '技术职称' },
            { key: 'address', label: '联系地址' },
            { key: 'postalCode', label: '邮编' },
            { key: 'mobile', label: '手机' },
            { key: 'officePhone', label: '办公电话' },
            { key: 'email', label: 'Email' }
          ]
        }
      ],
      mobileRepeatGroups: [
        {
          title: '主要学历',
          key: 'educationList',
          fields: [
            { key: 'period', label: '起止年月' },
            { key: 'school', label: '院校名称' },
            { key: 'degree', label: '学位' }
          ]
        },
        {
          title: '主要工作经历',
          key: 'workList',
          fields: [
            { key: 'period', label: '起止年月' },
            { key: 'company', label: '工作单位' },
            { key: 'position', label: '职务和技术职称' }
          ]
        },
        {
          title: '主要培训经历',
          key: 'trainingList',
          fields: [
            { key: 'period', label: '起止时间' },
            { key: 'course', label: '培训项目/课程名称' },
            { key: 'certified', label: '是否为注册系统认证课程', type: 'radio' }
          ]
        }
      ]
    }
  },
  created() {
    this.initializePage()
  },
  methods: {
    async initializePage() {
      const id = Number(this.$route.query.id || 0)
      if (id > 0) {
        this.isEditMode = true
        this.currentStep = 1
        this.pageLoading = true
      }
      try {
        await Promise.all([
          this.loadCommittees(),
          this.loadApplications()
        ])
        if (id > 0) {
          await this.editApplication({ id }, { silent: true, showLoading: false })
        }
      } finally {
        if (id > 0) {
          this.pageLoading = false
        }
      }
    },
    async loadApplications() {
      this.listLoading = true
      try {
        const res = await candidateRecommend.getList({ page: 1, pagesize: 100 })
        this.applicationList = res && res.data ? (res.data.data || []) : []
      } finally {
        this.listLoading = false
      }
    },
    async loadCommittees() {
      try {
        const res = await dictApi.getOptions({ ident: 'candidate_committee', tree: 1 })
        if (res && Array.isArray(res.data)) {
          this.committeeOptions = this.normalizeCommitteeTree(res.data)
        }
      } catch (error) {
        ElMessage.error('专委会列表加载失败，请刷新后重试')
      }
    },
    normalizeCommitteeTree(list) {
      return list.map(item => {
        const node = {
          id: item.id,
          name: item.name
        }
        if (Array.isArray(item.children) && item.children.length > 0) {
          node.children = this.normalizeCommitteeTree(item.children)
        }
        return node
      })
    },
    getCommitteeChildren(group) {
      return Array.isArray(group.children) ? group.children : []
    },
    chooseCommittee(group, item) {
      const exists = this.applicationList.find(row => row.committee === item.name && Number(row.id) !== Number(this.editingId))
      if (exists) {
        ElMessage.warning('您已提交过该专委会的申请，请返回申请列表修改原申请')
        return
      }
      this.committeeValue = [group.name, item.name]
      this.form.committee = item.name
    },
    isCommitteeSelected(item) {
      return this.form.committee === item.name
    },
    goNextStep() {
      if (!this.form.committee || this.committeeValue.length < 2) {
        ElMessage.warning('请先选择到具体委员会')
        return
      }
      const exists = this.applicationList.find(row => row.committee === this.form.committee && Number(row.id) !== Number(this.editingId))
      if (exists) {
        ElMessage.warning('同一个专委会只能提交一次申请，请返回申请列表修改原申请')
        return
      }
      if (!this.form.serialNo) {
        this.form.serialNo = this.generateSerialNo()
      }
      this.currentStep = 1
    },
    generateSerialNo() {
      const now = new Date()
      const pad = (value, length = 2) => String(value).padStart(length, '0')
      return [
        now.getFullYear(),
        pad(now.getMonth() + 1),
        pad(now.getDate()),
        pad(now.getHours()),
        pad(now.getMinutes()),
        pad(now.getSeconds()),
        pad(now.getMilliseconds(), 3)
      ].join('')
    },
    async handlePhotoChange(file) {
      const raw = file.raw
      if (!raw) return
      if (!raw.type.startsWith('image/')) {
        ElMessage.warning('请上传图片文件')
        return
      }
      const formData = new FormData()
      formData.append('file', raw)
      this.photoUploading = true
      try {
        const res = await attachment.uploadFile(formData)
        const photoUrl = res && res.data ? (res.data.url || (res.data[0] && res.data[0].url)) : ''
        if (photoUrl) {
          this.form.photoUrl = photoUrl
          ElMessage.success('照片上传成功')
        }
      } finally {
        this.photoUploading = false
      }
    },
    resetForm() {
      ElMessageBox.confirm('确定清空当前填写内容吗？', '提示', {
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }).then(() => {
        if (this.isEditMode && this.editingId) {
          this.editApplication({ id: this.editingId })
          return
        }
        this.form = defaultForm()
        this.isEditMode = false
        this.editingId = 0
        this.committeeValue = []
        this.currentStep = 0
        this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
      }).catch(() => {})
    },
    startNewApplication() {
      this.form = defaultForm()
      this.isEditMode = false
      this.editingId = 0
      this.committeeValue = []
      this.currentStep = 0
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    goList() {
      this.$router.push('/user/candidate-recommend')
    },
    async editApplication(row, options = {}) {
      const { silent = false, showLoading = true } = options
      if (Number(row.status) === 2) {
        ElMessage.warning('审核通过的申请不可修改')
        return
      }
      if (showLoading) {
        this.pageLoading = true
      }
      try {
        const res = await candidateRecommend.getDetail({ id: row.id })
        if (!res || !res.data) {
          ElMessage.error('申请信息加载失败，请返回列表后重试')
          return
        }
        if (Number(res.data.status) === 2) {
          ElMessage.warning('审核通过的申请不可修改')
          this.goList()
          return
        }
        this.form = this.normalizeDetailToForm(res.data)
        this.isEditMode = true
        this.editingId = Number(row.id)
        this.committeeValue = this.findCommitteeValue(this.form.committee)
        this.currentStep = 1
        this.$nextTick(() => {
          this.$refs.formRef && this.$refs.formRef.clearValidate()
          window.scrollTo({ top: 0, behavior: 'smooth' })
        })
        if (!silent) {
          ElMessage.success('已载入申请内容，可继续修改')
        }
      } catch (error) {
        ElMessage.error('申请信息加载失败，请稍后重试')
      } finally {
        if (showLoading) {
          this.pageLoading = false
        }
      }
    },
    normalizeDetailToForm(detail) {
      const toRows = (rows, count, emptyRow, mapper) => {
        const list = Array.isArray(rows) ? rows.map(mapper) : []
        while (list.length < count) {
          list.push({ ...emptyRow })
        }
        return list.slice(0, count)
      }
      return {
        ...defaultForm(),
        committee: detail.committee || '',
        serialNo: detail.serial_no || '',
        name: detail.name || '',
        gender: detail.gender || '',
        birthday: detail.birthday || '',
        nativePlace: detail.native_place || '',
        nation: detail.nation || '',
        idNumber: detail.id_number || '',
        joinDate: detail.join_date || '',
        memberNo: detail.member_no || '',
        company: detail.company || '',
        title: detail.title || '',
        address: detail.address || '',
        postalCode: detail.postal_code || '',
        mobile: detail.mobile || '',
        officePhone: detail.office_phone || '',
        email: detail.email || '',
        photoUrl: detail.photo || '',
        educationList: toRows(detail.education_list || detail.educationList, 3, { period: '', school: '', degree: '' }, item => ({
          period: item.period || '',
          school: item.school || '',
          degree: item.degree || ''
        })),
        workList: toRows(detail.work_list || detail.workList, 3, { period: '', company: '', position: '' }, item => ({
          period: item.period || '',
          company: item.company || '',
          position: item.position || ''
        })),
        trainingList: toRows(detail.training_list || detail.trainingList, 4, { period: '', course: '', certified: '' }, item => ({
          period: item.period || '',
          course: item.course || '',
          certified: item.certified === 1 || item.certified === '1' ? '是' : (item.certified === 2 || item.certified === '2' ? '否' : (item.certified || ''))
        })),
        partTimeJob: detail.part_time_job || '',
        foreignLanguage: detail.foreign_language || '',
        promise: Number(detail.promise) === 1
      }
    },
    findCommitteeValue(committee) {
      for (const group of this.committeeOptions) {
        const children = this.getCommitteeChildren(group)
        if (children.some(item => item.name === committee)) {
          return [group.name, committee]
        }
      }
      return committee ? ['', committee] : []
    },
    validateRequiredRows(list, fields, title) {
      let completeRows = 0
      for (let index = 0; index < list.length; index += 1) {
        const row = list[index]
        const hasValue = fields.some(field => String(row[field.key] || '').trim())
        if (!hasValue) {
          continue
        }
        const emptyField = fields.find(field => !String(row[field.key] || '').trim())
        if (emptyField) {
          ElMessage.warning(`${title}第${index + 1}行请填写${emptyField.label}`)
          return false
        }
        completeRows += 1
      }
      if (completeRows === 0) {
        ElMessage.warning(`请至少填写1行${title}`)
        return false
      }
      return true
    },
    validateRepeatGroups() {
      return this.validateRequiredRows(this.form.educationList, [
        { key: 'period', label: '起止年月' },
        { key: 'school', label: '院校名称' },
        { key: 'degree', label: '学位' }
      ], '主要学历') &&
        this.validateRequiredRows(this.form.workList, [
          { key: 'period', label: '起止年月' },
          { key: 'company', label: '工作单位' },
          { key: 'position', label: '职务和技术职称' }
        ], '主要工作经历') &&
        this.validateRequiredRows(this.form.trainingList, [
          { key: 'period', label: '起止时间' },
          { key: 'course', label: '培训项目/课程名称' },
          { key: 'certified', label: '是否为注册系统认证课程' }
        ], '主要培训经历')
    },
    validatePhoto() {
      if (this.photoUploading) {
        ElMessage.warning('照片正在上传，请稍后提交')
        return false
      }
      if (!this.form.photoUrl) {
        ElMessage.warning('请上传照片')
        return false
      }
      return true
    },
    submitForm() {
      this.form.memberNo = String(this.form.memberNo || '').trim().toUpperCase()
      if (!this.form.committee) {
        ElMessage.warning('请先选择专委会')
        this.currentStep = 0
        return
      }
      this.$refs.formRef.validate((valid) => {
        if (!valid) {
          ElMessage.warning('请检查并完善表单中的必填项')
          return
        }
        if (!this.validateRepeatGroups()) return
        if (!this.validatePhoto()) return
        if (!this.form.promise) {
          ElMessage.warning('请勾选本人承诺')
          return
        }
        ElMessageBox.confirm(this.editingId ? '确认提交修改后的委员推荐申请吗？' : '确认提交委员推荐申请吗？', '提交确认', {
          type: 'warning',
          confirmButtonText: '确认提交',
          cancelButtonText: '再检查一下'
        }).then(() => {
          this.submitRecommend()
        }).catch(() => {})
      })
    },
    async submitRecommend() {
      if (this.submitting) return
      this.submitting = true
      try {
        const res = this.editingId
          ? await candidateRecommend.update({ ...this.form, id: this.editingId })
          : await candidateRecommend.submit(this.form)
        if (res) {
          ElMessage.success(res.msg || (this.editingId ? '修改成功' : '提交成功'))
          this.goList()
        }
      } finally {
        this.submitting = false
      }
    },
    getStatusLabel(status) {
      const map = { 0: '草稿', 1: '已提交', 2: '审核通过', 3: '审核驳回' }
      return map[Number(status)] || '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-recommend-page {
  min-height: 100vh;
  padding: 28px 16px 48px;
  background: #f5f7fa;
  color: #111;
}

.form-shell {
  max-width: 980px;
  margin: 0 auto;
  padding: 28px;
  background: #fff;
  box-shadow: 0 12px 36px rgba(31, 45, 61, 0.08);
}

.form-tip {
  margin-bottom: 18px;
}

.form-steps {
  margin-bottom: 28px;
}

.committee-step {
  h1 {
    margin: 0 0 22px;
    font-size: 24px;
    text-align: center;
  }
}

.committee-groups {
  display: grid;
  gap: 22px;
}

.committee-group {
  padding: 18px 20px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fbfdff;

  h2 {
    margin: 0 0 14px;
    padding-left: 10px;
    border-left: 4px solid #409eff;
    font-size: 18px;
    line-height: 1.3;
    color: #111827;
  }
}

.committee-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

.committee-card {
  min-height: 44px;
  padding: 8px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  color: #303133;
  font-size: 16px;
  line-height: 1.35;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.16s ease, color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;

  &.active {
    border-color: #409eff;
    background: #ecf5ff;
    color: #1682e6;
    box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.16);
  }

  &:hover {
    border-color: #409eff;
    color: #1682e6;
  }
}

.step-actions {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

.page-header {
  position: relative;
  margin-bottom: 10px;
  text-align: center;

  .association-line {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.5;
  }

  .committee-name {
    min-width: 190px;
    padding: 0 8px 2px;
    border-bottom: 1px solid #111;
  }

  h1 {
    margin: 2px 0 0;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0;
  }

  .serial-no {
    position: static;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    max-width: 330px;
    margin: 6px 0 0 auto;
    font-size: 16px;
    line-height: 1.35;
    .span-width{width: 80px;}
    :deep(.el-input__wrapper) {
      box-shadow: none;
      border-bottom: 1px solid #111;
      border-radius: 0;
      padding: 0 4px;
    }

    :deep(.el-input__inner) {
      text-align: left;
      white-space: normal;
    }
  }
}

.paper-form {
  overflow-x: auto;
}

.recommend-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 16px;

  th,
  td {
    height: 36px;
    padding: 0 8px;
    border: 1px solid #111;
    vertical-align: middle;
    font-weight: 400;
  }

  th {
    text-align: center;
    background: #fff;
  }

  .vertical-title {
    position: relative;
    width: 38px;
    padding: 0;
    line-height: 1.45;
    letter-spacing: 0;

    span {
      position: absolute;
      top: 50%;
      left: 50%;
      writing-mode: vertical-rl;
      white-space: nowrap;
      word-break: keep-all;
      overflow-wrap: normal;
      transform: translate(-50%, -50%);
    }
  }

  .photo-cell {
    width: 118px;
    padding: 0;
    text-align: center;
  }

  :deep(.el-form-item) {
    margin: 0;
  }

  :deep(.el-form-item__content) {
    line-height: 1;
  }

  :deep(.el-input__wrapper),
  :deep(.el-textarea__inner) {
    min-height: 34px;
    padding: 0 4px;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
  }

  :deep(.el-input__inner) {
    height: 34px;
    line-height: 34px;
  }

  :deep(.el-date-editor.el-input) {
    width: 100%;
  }

  :deep(.el-radio) {
    margin-right: 22px;
  }
}

.photo-uploader,
.photo-placeholder,
.photo-preview {
  width: 100%;
  height: 178px;
}

.photo-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111;
}

.photo-preview {
  display: block;
  object-fit: cover;
}

.mobile-form {
  display: none;
}

.form-actions {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  padding: 14px 0 0;
  background: #fff;
}

@media (max-width: 768px) {
  .candidate-recommend-page {
    padding: 12px;
    background: #fff;
  }

  .form-shell {
    padding: 0;
    box-shadow: none;
  }

  .form-steps {
    margin-bottom: 18px;
  }

  .committee-step {
    h1 {
      font-size: 22px;
      text-align: left;
    }
  }

  .committee-group {
    padding: 14px 12px 16px;
  }

  .committee-grid {
    grid-template-columns: 1fr;
  }

  .committee-card {
    min-height: 42px;
    font-size: 15px;
  }

  .page-header {
    margin-bottom: 16px;
    padding-bottom: 14px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;

    .association-line {
      display: block;
      font-size: 18px;
      line-height: 1.8;
    }

    .committee-name {
      display: inline-block;
      min-width: 128px;
      vertical-align: middle;
    }

    h1 {
      margin-top: 8px;
      font-size: 22px;
      line-height: 1.3;
    }

    .serial-no {
      width: 100%;
      max-width: none;
      margin-top: 8px;
    }
  }

  .paper-form {
    display: none;
  }

  .mobile-form {
    display: block;
  }

  .mobile-section {
    padding: 16px 0;
    border-bottom: 8px solid #f3f4f6;

    h2 {
      margin: 0 0 12px;
      font-size: 17px;
      font-weight: 700;
      color: #111827;
    }

    :deep(.el-date-editor.el-input),
    :deep(.el-date-editor.el-input__wrapper) {
      width: 100%;
    }
  }

  .mobile-photo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 112px;
    height: 150px;
    border: 1px solid #d1d5db;
    color: #6b7280;
    background: #fafafa;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .repeat-card {
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
  }

  .form-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 12px 0;
    border-top: 1px solid #e5e7eb;

    .el-button {
      width: 100%;
      margin: 0;
    }

    .el-button--primary {
      grid-column: 1 / -1;
    }
  }
}
</style>
